/**
 * The Suspense boundary an agent's lazily-loaded screens land in, on whichever
 * surface they are mounted.
 *
 * Both mount points need one and both had written their own, already drifted:
 * a `h-dvh` centred spinner with a screen-reader label on the full-screen side,
 * a `h-full` one without on the frame side. The height is the one thing that
 * genuinely differs — full-screen the shell owns the viewport, in the frame it
 * owns a pane — and that is exactly what useIsFramed already answers.
 */
import type { ReactNode } from 'react'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'

import { Spinner } from '@/components/ui/spinner'
import { useIsFramed } from './agentSurface'

export function AgentScreenFallback({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const framed = useIsFramed()

  return (
    <Suspense
      fallback={
        <div
          className={`flex items-center justify-center bg-[var(--color-surface-canvas,#ffffff)] ${
            framed ? 'h-full' : 'h-dvh'
          }`}
        >
          <Spinner className="h-6 w-6 text-[var(--color-surface-mute)]" />
          <span className="sr-only">{t('common.loading', 'Loading...')}</span>
        </div>
      }
    >
      {children}
    </Suspense>
  )
}
